/**
 * WallScreen — «Стена» Рублокса.
 *
 * Своя лента, отдельная от школьной: содержимое здесь про игры и
 * сообщество Рублокса. Данные — из собственного бэкенда api.rublox.pro.
 *
 * Модерация: запись публикуется только после проверки. Интерфейс говорит
 * об этом прямо — иначе человек решит, что публикация не сработала, и
 * отправит текст ещё раз.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import * as wallApi from "../../../api/wall";
import CabIcon from "../CabIcon";
import { Button, Card, Segmented, EmptyState, Skeleton, Pill, fmtNum } from "../../ui";
import "../../../styles/cabinet-wall.css";

const MAX_LEN = 2000;

export default function WallScreen() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mine, setMine] = useState(false);
  const [error, setError] = useState(null);

  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  /* Успех и ошибка — РАЗНЫЕ состояния. Раньше оба писались в один
     `notice` и рендерились одинаковым синим блоком: провал публикации
     выглядел как «всё хорошо». Плюс сообщение висело до следующей
     отправки и не гасло само. */
  const [notice, setNotice] = useState(null);
  const [postError, setPostError] = useState(null);

  // Отменяем запрос при смене вкладки: иначе ответ старого фильтра
  // может прийти после нового и перезаписать список.
  const abortRef = useRef(null);

  const load = useCallback(
    async (opts = {}) => {
      const { append = false, cur = null, onlyMine = mine } = opts;
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      setLoading(true);
      setError(null);
      try {
        const data = await wallApi.getWall({
          cursor: cur,
          mine: onlyMine,
          signal: ctrl.signal,
        });
        const list = Array.isArray(data?.posts) ? data.posts : [];
        setPosts((prev) => (append ? [...prev, ...list] : list));
        setCursor(data?.next_cursor || null);
      } catch (e) {
        if (e?.name === "AbortError") return;
        setError(e?.message || "Не удалось загрузить ленту");
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    },
    [mine]
  );

  useEffect(() => {
    load({ onlyMine: mine });
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [mine, load]);

  const submit = async () => {
    const value = text.trim();
    if (!value || sending) return;
    if (!isAuthenticated) {
      navigate("/login?return=" + encodeURIComponent("/app/wall"));
      return;
    }
    setSending(true);
    setNotice(null);
    setPostError(null);
    try {
      await wallApi.createPost(value);
      // Текст очищаем только после успеха: иначе при ошибке человек
      // теряет написанное и не видит записи в ленте.
      setText("");
      // Говорим прямо, что запись ушла на проверку: иначе человек не
      // увидит её в ленте и отправит второй раз.
      setNotice("Запись отправлена на проверку — появится в ленте после модерации.");
      if (mine) load({ onlyMine: true });
    } catch (e) {
      setPostError(e?.message || "Не удалось опубликовать запись");
    } finally {
      setSending(false);
    }
  };

  /* Кнопка из пустого состояния ставит курсор в композер: раньше там
     был только совет «напиши первую запись», а поле находилось выше и
     человеку приходилось искать его глазами. */
  const composerRef = useRef(null);
  const focusComposer = () => {
    composerRef.current?.focus();
    composerRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  };

  /* Сообщение об успехе гаснет само. Висящая плашка «отправлено на
     проверку» через десять минут работы уже вводит в заблуждение. */
  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 8000);
    return () => clearTimeout(t);
  }, [notice]);

  const left = MAX_LEN - text.length;

  return (
    <div className="rb-wall">
      <div className="rb-wall__head">
        <h1 className="rb-wall__title">
          <CabIcon name="home" size={22} /> Стена
        </h1>
        {/* Segmented, а не Tabs: это ОДНО содержимое (лента) в двух
            выборках, ровно случай сегмент-контрола из дизайн-системы.
            Гостю «Мои» не показываем вовсе — выключенная кнопка без
            объяснения выглядит поломкой. */}
        {isAuthenticated ? (
          <Segmented
            items={[
              { id: "all", label: "Все" },
              { id: "mine", label: "Мои" },
            ]}
            value={mine ? "mine" : "all"}
            onChange={(v) => setMine(v === "mine")}
          />
        ) : null}
      </div>

      {/* Композер. Гостю показываем, но при попытке писать уводим на вход —
          витрина видна всем, авторизация требуется в момент действия. */}
      <Card className="rb-wall__composer">
        <textarea
          ref={composerRef}
          className="ui-textarea rb-wall__textarea"
          placeholder={
            isAuthenticated
              ? "Что нового? Поделись с сообществом Рублокса…"
              : "Войди, чтобы оставить запись"
          }
          value={text}
          maxLength={MAX_LEN}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => {
            if (!isAuthenticated) {
              navigate("/login?return=" + encodeURIComponent("/app/wall"));
            }
          }}
          rows={3}
        />
        <div className="rb-wall__composer-foot">
          <span className="rb-pill rb-pill--neutral">
            <CabIcon name="shield" size={12} /> на модерации
          </span>
          <div className="rb-wall__composer-actions">
            {text.length > MAX_LEN - 200 && (
              <span className="rb-wall__counter ui-num">{left}</span>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={submit}
              loading={sending}
              disabled={!text.trim()}
            >
              Опубликовать
            </Button>
          </div>
        </div>
        {/* Успех и ошибка выглядят по-разному. Раньше оба шли одним синим
            блоком, и «Не удалось опубликовать» читалось как успех. */}
        {notice && <div className="rb-wall__notice">{notice}</div>}
        {postError && <div className="rb-wall__post-error">{postError}</div>}
      </Card>

      {/* Ошибка загрузки ленты — с выходом, а не голой строкой. */}
      {error ? (
        <EmptyState
          icon={<CabIcon name="warning" size={40} />}
          title="Лента не загрузилась"
          desc="Проверь соединение и попробуй ещё раз."
          action={
            <Button variant="primary" onClick={() => load({ onlyMine: mine })}>
              Повторить
            </Button>
          }
        />
      ) : loading && posts.length === 0 ? (
        /* Скелетон только при ПЕРВОЙ загрузке: при догрузке страницы
           список не должен подменяться заглушками. */
        <div className="rb-wall__list">
          {[0, 1, 2].map((i) => (
            <Card key={i}>
              <Skeleton h={14} w="40%" />
              <Skeleton h={12} w="90%" style={{ marginTop: 10 }} />
              <Skeleton h={12} w="70%" style={{ marginTop: 6 }} />
            </Card>
          ))}
        </div>
      ) : posts.length === 0 ? (
        <EmptyState
          icon={<CabIcon name="home" size={40} />}
          title={mine ? "Ты ещё ничего не публиковал" : "Здесь пока пусто"}
          desc={
            mine
              ? "Напиши первую запись — она появится в ленте после проверки."
              : "Стань первым, кто напишет на стене Рублокса."
          }
          action={
            isAuthenticated ? (
              <Button variant="primary" onClick={focusComposer}>
                Написать запись
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={() =>
                  navigate("/login?return=" + encodeURIComponent("/app/wall"))
                }
              >
                Войти
              </Button>
            )
          }
        />
      ) : (
        <div className="rb-wall__list">
          {posts.map((p) => (
            <PostCard key={p.id} post={p} meId={user?.userId} />
          ))}
        </div>
      )}

      {cursor && !error && (
        <div className="rb-wall__more">
          <Button
            variant="ghost"
            onClick={() => load({ append: true, cur: cursor })}
            loading={loading}
          >
            Показать ещё
          </Button>
        </div>
      )}
    </div>
  );
}

/** Одна запись. Просмотр засчитывается, когда карточка реально показалась. */
function PostCard({ post, meId }) {
  const ref = useRef(null);
  const counted = useRef(false);
  const [views, setViews] = useState(post.views_count || 0);

  useEffect(() => {
    if (!ref.current || counted.current) return;
    if (typeof IntersectionObserver === "undefined") return;

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || counted.current) return;
        counted.current = true;
        // Отключаемся сразу: просмотр засчитывается один раз, дальше
        // наблюдать нечего.
        io.disconnect();
        wallApi
          .registerView(post.id)
          .then((r) => {
            if (typeof r?.views_count === "number") setViews(r.views_count);
          })
          .catch(() => {
            /* просмотр не критичен — молчим */
          });
      },
      { threshold: 0.5 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [post.id]);

  /* Статус модерации — ЛИЧНАЯ информация автора: «На проверке» и тем
     более «Отклонено: причина» посторонним видеть незачем.
     Раньше `meId` передавался в компонент, но в сигнатуре его не было,
     то есть проверка автора не выполнялась вовсе — комментарий обещал
     защиту, которой не существовало. Полагаться только на фильтрацию
     бэкенда нельзя: гарантию ставим там, где происходит показ. */
  const isMine = meId != null && String(post.author_id) === String(meId);
  const pending = isMine && post.status === "pending";
  const rejected = isMine && post.status === "rejected";

  return (
    <article ref={ref} className="rb-card rb-wall__post">
      <div className="rb-wall__post-head">
        <span className="rb-wall__post-author">
          {post.author_name || `Игрок #${post.author_id}`}
        </span>
        <span className="rb-wall__post-time">
          {wallApi.timeAgo(post.created_at)}
        </span>
      </div>

      {pending && (
        <Pill
          tone="warning"
          className="rb-wall__post-status"
          icon={<CabIcon name="shield" size={13} />}
        >
          На проверке
        </Pill>
      )}
      {rejected && (
        <Pill tone="danger" className="rb-wall__post-status">
          Отклонено{post.reject_reason ? `: ${post.reject_reason}` : ""}
        </Pill>
      )}

      <div className="rb-wall__post-text">{post.text}</div>

      <div className="rb-wall__post-foot">
        {/* Форматирование обязательно: 12345 просмотров без разделителя
            читается как «двенадцать тысяч триста сорок пять» только после
            пересчёта разрядов глазами. */}
        <span className="rb-wall__post-views ui-num">
          <CabIcon name="eye" size={14} /> {fmtNum(views)}
        </span>
      </div>
    </article>
  );
}
